import {
  DownloadOutlined,
  PieChartOutlined,
  TableOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Empty,
  Modal,
  Progress,
  Radio,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import apiCall from 'apps/investment/apiCalls/userProgram';
import transactionApiCall from 'apps/investment/apiCalls/transaction';
import { orderTypeColors, orderTypeLabels } from 'apps/investment/constants/fundOrder';
import { useFetchApiGet, useFetchApiList } from 'hooks/api';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from 'common/ui/chart';
import { getChangeValueColor } from 'utils/color';
import { formatNumber } from 'utils/formatAmount';
import { formatDate, formatDateTime, isZeroDate } from 'utils/formatDate';

const { Title, Text } = Typography;

// Helper function to validate dates (handles null, undefined, empty strings, zero dates, invalid dates)
const isValidDate = dateValue => {
  // Check for null, undefined, or empty string
  if (
    !dateValue ||
    dateValue === '' ||
    dateValue === null ||
    dateValue === undefined
  ) {
    return false;
  }

  // Convert to string and check for invalid patterns first
  const dateStr = String(dateValue).trim();

  // Check for common zero date string patterns and invalid strings
  if (
    dateStr === '0000-00-00' ||
    dateStr === '0001-01-01' ||
    dateStr === '1970-01-01' || // Unix epoch zero
    dateStr.startsWith('0000-') ||
    dateStr === '""' || // JSON empty string representation
    dateStr === 'null' ||
    dateStr === 'undefined' ||
    dateStr.toLowerCase() === 'invalid date' ||
    dateStr.toLowerCase().includes('invalid') ||
    dateStr === 'NaN' ||
    dateStr === 'Infinity' ||
    dateStr === '-Infinity'
  ) {
    return false;
  }

  // Try parsing as ISO string first (common format from backend)
  let parsed = moment(dateValue);

  // If that fails, try as Unix timestamp (only for numbers)
  if (!parsed.isValid() && typeof dateValue === 'number') {
    // Check if it's a reasonable timestamp (not zero or negative)
    if (dateValue <= 0 || dateValue > 2147483647) {
      return false; // Unix timestamp out of reasonable range
    }
    parsed = moment.unix(dateValue);
  }

  // If still invalid, return false
  if (!parsed.isValid()) {
    return false;
  }

  // Check if the formatted result contains "Invalid date" (moment.js sometimes returns this)
  const formattedCheck = parsed.format('YYYY-MM-DD');
  if (
    formattedCheck === 'Invalid date' ||
    formattedCheck.toLowerCase().includes('invalid') ||
    formattedCheck === 'NaN-NaN-NaN' ||
    formattedCheck.includes('NaN')
  ) {
    return false;
  }

  // Check if it's a zero or invalid year
  const year = parsed.year();
  if (
    isNaN(year) ||
    !isFinite(year) ||
    year === 0 ||
    year === 1 ||
    year < 1900 ||
    year > 2100 // Reasonable upper bound
  ) {
    return false;
  }

  // Additional check: verify the date can be formatted correctly
  try {
    const testFormat = parsed.format('DD/MM/YYYY');
    if (
      !testFormat ||
      testFormat === 'Invalid date' ||
      testFormat.toLowerCase().includes('invalid') ||
      testFormat.includes('NaN')
    ) {
      return false;
    }
  } catch (error) {
    return false;
  }

  // Final check: use isZeroDate utility (but only if we've validated it's parseable)
  // Wrap in try-catch since isZeroDate might format the date and could throw
  try {
    if (isZeroDate(dateValue)) {
      return false;
    }
  } catch (error) {
    // If isZeroDate throws an error, consider the date invalid
    return false;
  }

  return true;
};

// Safe date formatter that handles all edge cases
const formatDateSafe = dateValue => {
  // First check if the value itself is invalid
  if (!isValidDate(dateValue)) {
    return 'N/A';
  }

  // Try to format the date
  let formatted;
  try {
    formatted = formatDate(dateValue);
  } catch (error) {
    return 'N/A';
  }

  // Check if the formatted result is invalid or contains invalid patterns
  if (
    !formatted ||
    typeof formatted !== 'string' ||
    formatted === 'Invalid date' ||
    formatted.toLowerCase().includes('invalid') ||
    formatted === 'null' ||
    formatted === 'undefined' ||
    formatted === 'NaN' ||
    formatted.includes('NaN') ||
    formatted.trim() === '' ||
    formatted.toLowerCase() === 'invalid date'
  ) {
    return 'N/A';
  }

  return formatted;
};

const sharedColumns = [
  {
    title: 'Subscription Amount',
    dataIndex: 'subscription_amount',
    align: 'right',
    render: value => (
      <span className="font-medium">
        {new Intl.NumberFormat('en-US').format(value)}
      </span>
    ),
  },
  {
    title: 'Quantity',
    dataIndex: 'quantity',
    align: 'right',
    render: value => (
      <span className="font-medium">
        {new Intl.NumberFormat('en-US').format(value)}
      </span>
    ),
  },
  {
    title: 'Market Value',
    dataIndex: 'market_value',
    align: 'right',
    render: value => (
      <span className="font-medium">
        {new Intl.NumberFormat('en-US').format(value)}
      </span>
    ),
  },
  {
    title: 'P/L',
    dataIndex: 'gain_loss_amount',
    align: 'right',
    render: (value, record) => (
      <span
        className="font-semibold"
        style={{ color: getChangeValueColor(value) }}
      >
        {new Intl.NumberFormat('en-US').format(value)}
      </span>
    ),
  },
  {
    title: 'P/L %',
    dataIndex: 'gain_loss_percentage',
    align: 'right',
    render: value => (
      <span
        className="font-semibold"
        style={{ color: getChangeValueColor(value) }}
      >
        {new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value)}
        %
      </span>
    ),
  },
];

const transactionStatusColors = {
  pending: 'yellow',
  completed: 'green',
  cancelled: 'gray',
};

const transactionSourceColors = {
  stag: 'blue',
  nhsv: 'orange',
  fmarket: 'purple',
};

const transactionColumns = [
  {
    title: 'Date',
    dataIndex: 'date',
    render: value => formatDateTime(value),
  },
  {
    title: 'Type',
    dataIndex: 'type',
    render: value => (
      <Tag color={orderTypeColors[value]}>{orderTypeLabels[value] || value}</Tag>
    ),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    render: value => (
      <Tag color={transactionStatusColors[value]}>{value}</Tag>
    ),
  },
  {
    title: 'Source',
    dataIndex: 'source',
    render: value => (
      <Tag color={transactionSourceColors[value]}>{value?.toUpperCase()}</Tag>
    ),
  },
  {
    title: 'Product',
    dataIndex: 'product_code',
    className: 'font-medium',
    render: (value, record) => value || record.product_alias,
  },
  {
    title: 'Amount',
    dataIndex: 'amount',
    align: 'right',
    render: value => formatNumber(value),
  },
  {
    title: 'Net Amount',
    dataIndex: 'net_amount',
    align: 'right',
    render: value => formatNumber(value),
  },
  {
    title: 'Quantity',
    dataIndex: 'quantity',
    align: 'right',
    render: value => formatNumber(value),
  },
  {
    title: 'Price',
    dataIndex: 'price',
    align: 'right',
    render: value => formatNumber(value),
  },
];

const SLICE_COLORS = [
  '#e03131',
  '#1971c2',
  '#f59f00',
  '#2f9e44',
  '#ae3ec9',
  '#e8590c',
  '#0c8599',
  '#c2255c',
  '#6741d9',
  '#74b816',
];

const industryColumns = [
  {
    title: 'Ngành',
    dataIndex: 'industry_name',
    render: (value, record, index) => (
      <div className="flex items-center gap-2">
        <span
          className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
          style={{ backgroundColor: SLICE_COLORS[index % SLICE_COLORS.length] }}
        />
        {value}
      </div>
    ),
  },
  {
    title: 'Số mã CK',
    dataIndex: 'stock_count',
    align: 'right',
    render: value => formatNumber(value),
  },
  {
    title: 'Giá trị thị trường',
    dataIndex: 'market_value',
    align: 'right',
    render: value => <span className="font-mono">{formatNumber(value)}</span>,
  },
  {
    title: 'Tỷ trọng (%)',
    dataIndex: 'allocation_percentage',
    align: 'right',
    render: value => <span className="font-mono">{formatNumber(value)}%</span>,
  },
];

const symbolAllocationColumns = [
  { title: 'Mã CK', dataIndex: 'symbol', className: 'font-medium' },
  { title: 'Tên', dataIndex: 'name' },
  {
    title: 'Số lượng',
    dataIndex: 'quantity',
    align: 'right',
    render: value =>
      value == null || isNaN(value) ? '-' : Math.ceil(value).toLocaleString('en-US'),
  },
  {
    title: 'Giá trị thị trường',
    dataIndex: 'market_value',
    align: 'right',
    render: value => <span className="font-mono">{formatNumber(value)}</span>,
  },
  {
    title: 'Tỷ trọng (%)',
    dataIndex: 'allocation_percentage',
    align: 'right',
    render: value => <span className="font-mono">{formatNumber(value)}%</span>,
  },
];

// Too many symbols to fit a legend without breaking the layout; rely on the
// tooltip for identification instead — mirrors the industry/symbol pie
// charts on the per-user Portfolio Analysis page.
function IndustryAllocationChart({ byIndustry }) {
  if (!byIndustry || byIndustry.length === 0) {
    return null;
  }

  const config = Object.fromEntries(
    byIndustry.map((item, i) => [
      item.industry_name,
      {
        label: `${item.industry_name} (${item.allocation_percentage.toFixed(2)}%)`,
        color: SLICE_COLORS[i % SLICE_COLORS.length],
      },
    ])
  );

  return (
    <ChartContainer config={config} className="mx-auto h-72 w-full max-w-sm">
      <PieChart>
        <Pie
          data={byIndustry}
          dataKey="allocation_percentage"
          nameKey="industry_name"
          cx="50%"
          cy="50%"
          innerRadius="55%"
          outerRadius="80%"
          paddingAngle={2}
          strokeWidth={0}
        >
          {byIndustry.map((item, i) => (
            <Cell key={item.industry_code} fill={SLICE_COLORS[i % SLICE_COLORS.length]} />
          ))}
        </Pie>
        <ChartTooltip content={<ChartTooltipContent nameKey="industry_name" />} />
      </PieChart>
    </ChartContainer>
  );
}

function SymbolAllocationChart({ bySymbol }) {
  if (!bySymbol || bySymbol.length === 0) {
    return null;
  }

  const config = Object.fromEntries(
    bySymbol.map((item, i) => [
      item.symbol,
      {
        label: `${item.symbol} (${item.allocation_percentage.toFixed(2)}%)`,
        color: SLICE_COLORS[i % SLICE_COLORS.length],
      },
    ])
  );

  return (
    <ChartContainer config={config} className="mx-auto h-72 w-full max-w-sm">
      <PieChart>
        <Pie
          data={bySymbol}
          dataKey="allocation_percentage"
          nameKey="symbol"
          cx="50%"
          cy="50%"
          innerRadius="55%"
          outerRadius="80%"
          paddingAngle={2}
          strokeWidth={0}
        >
          {bySymbol.map((item, i) => (
            <Cell key={item.symbol} fill={SLICE_COLORS[i % SLICE_COLORS.length]} />
          ))}
        </Pie>
        <ChartTooltip content={<ChartTooltipContent nameKey="symbol" />} />
      </PieChart>
    </ChartContainer>
  );
}

const CompactStat = ({ label, value, color }) => (
  <div className="min-w-[110px] text-right">
    <div className="text-[11px] uppercase tracking-wide text-gray-400">
      {label}
    </div>
    {/* Tailwind is configured with important:true, so a text color class
        would override the inline style — only apply gray when no color. */}
    <div
      className={`text-base font-semibold ${color ? '' : 'text-gray-800'}`}
      style={color ? { color } : undefined}
    >
      {value}
    </div>
  </div>
);

// Green for gain, red for loss — always, regardless of missing/zero values.
const gainLossColor = value => (Number(value) < 0 ? '#ef4444' : '#22c55e');

const PORTFOLIO_COLOR = '#2563eb';
const BENCHMARK_COLOR = '#ef4444';

const PerformanceTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const point = payload[0]?.payload || {};

  const renderIndex = (name, value, color) => {
    if (value === null || value === undefined) {
      return null;
    }
    const changePct = value - 100;
    return (
      <div className="flex items-center justify-between gap-4" key={name}>
        <span className="flex items-center gap-1.5 text-gray-600">
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ backgroundColor: color }}
          />
          {name}
        </span>
        <span className="font-mono font-semibold">
          {formatNumber(value)}{' '}
          <span style={{ color: getChangeValueColor(changePct) }}>
            ({changePct >= 0 ? '+' : ''}
            {formatNumber(changePct)}%)
          </span>
        </span>
      </div>
    );
  };

  return (
    <div className="rounded border border-gray-200 bg-white px-3 py-2 text-xs shadow-md">
      <div className="mb-1 font-medium text-gray-800">
        {moment(label).format('DD/MM/YYYY')}
      </div>
      {renderIndex('Danh mục', point.portfolio_index, PORTFOLIO_COLOR)}
      {renderIndex('VNINDEX', point.benchmark_index, BENCHMARK_COLOR)}
      {point.market_value !== undefined && (
        <div className="mt-1 flex items-center justify-between gap-4 border-t border-gray-100 pt-1 text-gray-500">
          <span>Giá trị danh mục</span>
          <span className="font-mono">{formatNumber(point.market_value)}</span>
        </div>
      )}
    </div>
  );
};

const CHART_RANGES = [
  { key: '1m', label: '1M', months: 1 },
  { key: '3m', label: '3M', months: 3 },
  { key: '6m', label: '6M', months: 6 },
  { key: 'ytd', label: 'YTD' },
  { key: '1y', label: '1Y', months: 12 },
  { key: 'all', label: 'All' },
];

// Cut the full daily series down to the selected range and re-normalize both
// indexes to 100 at the range start, so every range reads as "performance
// since the beginning of this period".
const sliceAndRebase = (points, rangeKey) => {
  let filtered = points;
  if (rangeKey !== 'all' && points.length > 0) {
    const range = CHART_RANGES.find(r => r.key === rangeKey);
    const cutoff =
      rangeKey === 'ytd'
        ? moment().startOf('year')
        : moment().subtract(range.months, 'months');
    filtered = points.filter(p => moment(p.date).isSameOrAfter(cutoff, 'day'));
  }
  if (filtered.length < 2) {
    return filtered;
  }

  // Rebase both lines off the SAME point in time. The benchmark only has a
  // value on trading days, so when the range boundary lands on a weekend or
  // holiday, filtered[0] has no benchmark_index — using it for portfolioBase
  // while separately grabbing the first non-null point for benchmarkBase
  // would anchor the two lines to different calendar days (e.g. portfolio
  // pinned to Sunday's carried-forward value, benchmark pinned to the
  // following Monday's close), producing a fake gap between them.
  const anchorPoint =
    filtered.find(
      p => p.benchmark_index !== null && p.benchmark_index !== undefined
    ) || filtered[0];
  const portfolioBase = anchorPoint.portfolio_index;
  const benchmarkBase = anchorPoint.benchmark_index;

  return filtered.map(p => ({
    ...p,
    portfolio_index:
      Math.round((p.portfolio_index / portfolioBase) * 10000) / 100,
    benchmark_index:
      benchmarkBase && p.benchmark_index !== null &&
      p.benchmark_index !== undefined
        ? Math.round((p.benchmark_index / benchmarkBase) * 10000) / 100
        : null,
  }));
};

// Downsample a (already rebased) daily series to one point per week: the last
// day with a benchmark value (= last trading day; both series are flat over
// the weekend anyway), falling back to the week's last day. Keeps the first
// point so the chart still starts at 100.
const toWeekly = points => {
  if (points.length === 0) {
    return points;
  }
  const weekly = [points[0]];
  const byWeek = new Map();
  points.forEach(p => {
    const week = moment(p.date).format('GGGG-WW');
    const current = byWeek.get(week);
    if (
      !current ||
      p.benchmark_index !== null ||
      current.benchmark_index === null
    ) {
      byWeek.set(week, p);
    }
  });
  byWeek.forEach(p => {
    if (p.date !== points[0].date) {
      weekly.push(p);
    }
  });
  return weekly.sort((a, b) => a.date.localeCompare(b.date));
};

// Performance of the goal portfolio vs VNINDEX, both normalized to 100 at the
// range start (time-weighted, so recurring contributions are not counted as
// performance).
const GoalPerformanceChart = ({
  title,
  points: sourcePoints,
  isLoading,
  weekly = false,
  showExplanation = false,
}) => {
  const [range, setRange] = useState('all');
  const [isTableOpen, setIsTableOpen] = useState(false);

  const allPoints = useMemo(() => sourcePoints || [], [sourcePoints]);
  const points = useMemo(() => {
    const rebased = sliceAndRebase(allPoints, range);
    return weekly ? toWeekly(rebased) : rebased;
  }, [allPoints, range, weekly]);

  if (isLoading) {
    return (
      <Card className="shadow-sm">
        <div className="flex justify-center items-center py-12">
          <Spin />
        </div>
      </Card>
    );
  }

  if (allPoints.length < 2) {
    return null;
  }

  const exportCsv = () => {
    const header = 'date,portfolio_index,benchmark_index,market_value';
    const rows = points.map(p =>
      [
        p.date,
        p.portfolio_index,
        p.benchmark_index ?? '',
        p.market_value,
      ].join(',')
    );
    // BOM so Excel opens it as UTF-8.
    const blob = new Blob(['\ufeff' + [header, ...rows].join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `goal-performance-${weekly ? 'weekly' : 'daily'}-${range}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const tableColumns = [
    {
      title: 'Ngày',
      dataIndex: 'date',
      render: value => moment(value).format('DD/MM/YYYY'),
    },
    {
      title: 'Danh mục',
      dataIndex: 'portfolio_index',
      align: 'right',
      render: value => (
        <span
          className="font-mono"
          style={{ color: getChangeValueColor(value - 100) }}
        >
          {formatNumber(value)}
        </span>
      ),
    },
    {
      title: 'VNINDEX',
      dataIndex: 'benchmark_index',
      align: 'right',
      render: value =>
        value === null || value === undefined ? (
          '—'
        ) : (
          <span
            className="font-mono"
            style={{ color: getChangeValueColor(value - 100) }}
          >
            {formatNumber(value)}
          </span>
        ),
    },
    {
      title: 'Giá trị danh mục',
      dataIndex: 'market_value',
      align: 'right',
      render: value => <span className="font-mono">{formatNumber(value)}</span>,
    },
  ];

  const lastPoint = points[points.length - 1];
  const lastBenchmark = [...points]
    .reverse()
    .find(p => p.benchmark_index !== null && p.benchmark_index !== undefined);

  return (
    <Card
      className="shadow-sm"
      title={
        <Title level={5} className="mb-0 text-gray-800">
          {title}
        </Title>
      }
    >
      {/* Controls live in the body: the card header clips wrapped content. */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        <div className="flex items-center gap-4 text-sm">
          {points.length >= 2 && (
            <>
              <span>
                Danh mục:{' '}
                <span
                  className="font-semibold"
                  style={{
                    color: getChangeValueColor(lastPoint.portfolio_index - 100),
                  }}
                >
                  {lastPoint.portfolio_index >= 100 ? '+' : ''}
                  {formatNumber(lastPoint.portfolio_index - 100)}%
                </span>
              </span>
              {lastBenchmark && (
                <span>
                  VNINDEX:{' '}
                  <span
                    className="font-semibold"
                    style={{
                      color: getChangeValueColor(
                        lastBenchmark.benchmark_index - 100
                      ),
                    }}
                  >
                    {lastBenchmark.benchmark_index >= 100 ? '+' : ''}
                    {formatNumber(lastBenchmark.benchmark_index - 100)}%
                  </span>
                </span>
              )}
            </>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Radio.Group
            value={range}
            onChange={e => setRange(e.target.value)}
            options={CHART_RANGES.map(r => ({
              label: <span className="px-1">{r.label}</span>,
              value: r.key,
            }))}
            optionType="button"
            buttonStyle="solid"
          />
          <Button icon={<TableOutlined />} onClick={() => setIsTableOpen(true)}>
            Bảng dữ liệu
          </Button>
          <Button icon={<DownloadOutlined />} onClick={exportCsv}>
            CSV
          </Button>
        </div>
      </div>
      {points.length < 2 ? (
        <Empty description="Không đủ dữ liệu trong khoảng thời gian này" />
      ) : (
      <ResponsiveContainer width="100%" height={320}>
        <LineChart
          data={points}
          margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickFormatter={value => moment(value).format('DD/MM/YY')}
            minTickGap={48}
          />
          <YAxis
            domain={['auto', 'auto']}
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickFormatter={value => formatNumber(value)}
            width={56}
          />
          <RechartsTooltip content={<PerformanceTooltip />} />
          <Legend
            formatter={value =>
              value === 'portfolio_index' ? 'Danh mục' : 'VNINDEX'
            }
            wrapperStyle={{ fontSize: 12 }}
          />
          <ReferenceLine y={100} stroke="#9ca3af" strokeDasharray="4 4" />
          <Line
            type="monotone"
            dataKey="portfolio_index"
            stroke={PORTFOLIO_COLOR}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
          />
          <Line
            type="monotone"
            dataKey="benchmark_index"
            stroke={BENCHMARK_COLOR}
            strokeWidth={2}
            strokeDasharray="5 3"
            dot={false}
            activeDot={{ r: 4 }}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
      )}
      {showExplanation && (
        <div className="mt-3 rounded bg-gray-50 px-3 py-2 text-xs text-gray-500 leading-relaxed">
          <span className="font-medium text-gray-600">Cách so sánh: </span>
          Giá trị danh mục mỗi ngày được tái dựng từ các giao dịch đã hoàn tất
          và giá/NAV cuối ngày. Hiệu suất tính theo phương pháp Time-Weighted
          Return (TWR): lợi suất từng ngày được tính sau khi loại trừ tiền
          nạp/rút trong ngày, rồi nhân chuỗi lại — nhờ đó việc góp thêm tiền
          định kỳ không bị tính nhầm là tăng trưởng. Cả hai đường đều được
          chuẩn hóa = 100 tại ngày bắt đầu khoảng thời gian đang chọn, nên
          khoảng cách giữa đường Danh mục và đường VNINDEX thể hiện mức chênh
          lệch hiệu suất so với thị trường trong cùng kỳ.
        </div>
      )}
      {weekly && (
        <div className="mt-3 rounded bg-gray-50 px-3 py-2 text-xs text-gray-500">
          Mỗi điểm là ngày giao dịch cuối cùng của tuần.
        </div>
      )}
      <Modal
        title={title}
        open={isTableOpen}
        onCancel={() => setIsTableOpen(false)}
        footer={
          <Button icon={<DownloadOutlined />} onClick={exportCsv}>
            Xuất CSV
          </Button>
        }
        width={760}
      >
        <Table
          rowKey="date"
          columns={tableColumns}
          dataSource={[...points].reverse()}
          size="small"
          pagination={{ pageSize: 24, showSizeChanger: false }}
        />
      </Modal>
    </Card>
  );
};

// More than this many calendar days since an instrument's last price-history
// row is treated as stale (~1 trading week, so ordinary weekends don't flag).
const PRICE_STALE_DAYS_THRESHOLD = 5;

// Surfaces gaps in the price-history data (stock_price_daily / etf_price_daily /
// FundNavDaily) that the performance chart above is built from. When an
// instrument has no price row at all, its valuation stays pinned to its most
// recent transaction price instead of a real market price — this can make
// "Danh mục" performance diverge from (or even contradict the sign of) the
// goal's real Market Value / P&L, so surface it here rather than let it show
// up as a silent contradiction between the two.
const PriceDataStatusAlert = ({ items }) => {
  if (!items || items.length === 0) {
    return null;
  }

  const today = moment();
  const problems = items
    .map(item => {
      const lastDate = item.last_price_date ? moment(item.last_price_date) : null;
      const daysStale = lastDate ? today.diff(lastDate, 'days') : null;
      return {
        ...item,
        lastDate,
        daysStale,
        isMissing: !lastDate,
        isStale: !!lastDate && daysStale > PRICE_STALE_DAYS_THRESHOLD,
      };
    })
    .filter(item => item.isMissing || item.isStale);

  if (problems.length === 0) {
    return null;
  }

  return (
    <Alert
      type="warning"
      showIcon
      message="Thiếu dữ liệu giá lịch sử cho một số mã trong danh mục"
      description={
        <div>
          <div className="text-xs text-gray-500">
            Biểu đồ hiệu suất có thể không chính xác cho các mã dưới đây — giá
            trị được giữ nguyên theo giá giao dịch gần nhất thay vì giá thị
            trường cho đến khi có dữ liệu giá.
          </div>
          <ul className="mt-1 list-disc pl-5 text-sm">
            {problems.map(item => (
              <li key={`${item.product_type}-${item.product_code}`}>
                <span className="font-medium">
                  {item.product_code || item.product_alias}
                </span>{' '}
                {item.isMissing ? (
                  <span className="text-red-600">— chưa có dữ liệu giá</span>
                ) : (
                  <span className="text-orange-600">
                    — dữ liệu giá cũ nhất: {item.lastDate.format('DD/MM/YYYY')}{' '}
                    ({item.daysStale} ngày trước)
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      }
    />
  );
};

// Flags when the since-inception TWR (the "Danh mục" line, base 100) and the
// account's actual money-weighted P/L% diverge enough to look like a
// contradiction — either opposite signs, or a wide gap — and explains why
// both numbers can legitimately disagree: TWR tracks the NAV/price only,
// ignoring contribution timing, while P/L is on the money actually put in.
// For a goal that keeps contributing over time, a large share of the capital
// landing at a worse average price than the very first contribution is
// enough to flip the sign between the two.
const TWR_VS_MONEY_WEIGHTED_GAP_THRESHOLD = 8;

const TwrVsMoneyWeightedNote = ({ twrReturnPct, moneyWeightedReturnPct }) => {
  if (
    twrReturnPct === null ||
    twrReturnPct === undefined ||
    moneyWeightedReturnPct === null ||
    moneyWeightedReturnPct === undefined
  ) {
    return null;
  }

  const oppositeSign =
    (twrReturnPct > 0 && moneyWeightedReturnPct < 0) ||
    (twrReturnPct < 0 && moneyWeightedReturnPct > 0);
  const bigGap =
    Math.abs(twrReturnPct - moneyWeightedReturnPct) >=
    TWR_VS_MONEY_WEIGHTED_GAP_THRESHOLD;

  if (!oppositeSign && !bigGap) {
    return null;
  }

  return (
    <Alert
      type="info"
      showIcon
      message="Vì sao hiệu suất Danh mục khác với Total P/L?"
      description={
        <div className="text-sm text-gray-600 leading-relaxed">
          <span className="font-medium">Danh mục</span> ở biểu đồ bên dưới là{' '}
          <span className="font-medium">Time-Weighted Return (TWR)</span>,
          hiện là{' '}
          <span
            className="font-semibold"
            style={{ color: getChangeValueColor(twrReturnPct) }}
          >
            {twrReturnPct >= 0 ? '+' : ''}
            {formatNumber(twrReturnPct)}%
          </span>{' '}
          — đo biến động giá/NAV kể từ ngày đầu tiên, không phụ thuộc thời
          điểm nạp tiền. <span className="font-medium">Total P/L</span> ở
          trên là{' '}
          <span
            className="font-semibold"
            style={{ color: getChangeValueColor(moneyWeightedReturnPct) }}
          >
            {moneyWeightedReturnPct >= 0 ? '+' : ''}
            {formatNumber(moneyWeightedReturnPct)}%
          </span>{' '}
          trên số tiền thực tế đã nạp. Hai chỉ số này có thể lệch nhau — thậm
          chí ngược dấu — khi phần lớn vốn được nạp vào những thời điểm giá
          cao hơn mức trung bình kể từ ngày bắt đầu rồi sau đó giảm: khoản
          nạp sớm (thường nhỏ) có thể đã hưởng đà tăng ban đầu, còn phần vốn
          nạp sau (thường chiếm tỷ trọng lớn hơn) lại gánh chịu đợt giảm gần
          đây. Đây không phải lỗi tính toán — cả hai số đều đúng, chỉ trả
          lời hai câu hỏi khác nhau.
        </div>
      }
    />
  );
};

const GoalPortfolio = () => {
  const params = useParams();

  const {
    data: userProgramPortfolio,
    load: fetchUserProgramPortfolio,
    isLoading: isLoadingUserProgramPortfolio,
  } = useFetchApiGet(apiCall.portfolio, { resourceName: 'data' });
  const {
    data: userProgramPortfolioItems,
    load: fetchUserProgramPortfolioItems,
    isLoading: isLoadingUserProgramPortfolioItems,
  } = useFetchApiGet(apiCall.portfolioItems, { resourceName: 'data' });
  const {
    data: performanceResult,
    load: fetchPerformanceChart,
    isLoading: isLoadingPerformanceChart,
  } = useFetchApiGet(apiCall.performanceChart, { resourceName: 'data' });
  const performancePoints = performanceResult?.points || [];
  const priceDataStatus = performanceResult?.price_data_status || [];
  const {
    data: analysisResult,
    load: fetchAnalysis,
  } = useFetchApiGet(apiCall.analysis, { resourceName: 'data' });
  const {
    data: transactions,
    load: fetchTransactions,
    isLoading: isLoadingTransactions,
  } = useFetchApiList(transactionApiCall.list, { resourceName: 'data' });

  useEffect(() => {
    fetchUserProgramPortfolio({ id: params.id });
    fetchUserProgramPortfolioItems({ id: params.id });
    fetchPerformanceChart({ id: params.id });
    fetchAnalysis({ id: params.id });
    fetchTransactions({ goal_id: params.id, page_size: 200 });
  }, []);

  const isLoading =
    isLoadingUserProgramPortfolio || isLoadingUserProgramPortfolioItems;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Spin size="large" />
      </div>
    );
  }

  if (!userProgramPortfolio) {
    return <Empty description="No goal portfolio data" />;
  }

  const {
    user_goal: userGoal,
    total_gain_loss_amount: totalGainLossAmount,
    total_gain_loss_percentage: totalGainLossPercentage,
    total_market_value: totalMarketValue,
    total_subscription_amount: totalSubscriptionAmount,
  } = userProgramPortfolio;
  const lastPerformancePoint = performancePoints[performancePoints.length - 1];
  const twrReturnPct = lastPerformancePoint
    ? lastPerformancePoint.portfolio_index - 100
    : null;
  const {
    portfolio_fund_items: portfolioFundItems = [],
    portfolio_stock_items: portfolioStockItems = [],
  } = userProgramPortfolioItems;
  const byIndustry = analysisResult?.by_industry || [];
  const bySymbol = analysisResult?.by_symbol || [];

  const userId = userProgramPortfolio.user_id;

  return (
    <div className="space-y-6">
      {/* Shortcuts */}
      {userId && (
        <div className="flex justify-end gap-3 -mb-2">
          <Link to={`/robo-advisory/portfolios/${userId}`}>
            <Button icon={<PieChartOutlined />}>Xem danh mục</Button>
          </Link>
          <Link to={`/investment/users/${userId}`}>
            <Button icon={<UserOutlined />}>Chi tiết người dùng</Button>
          </Link>
        </div>
      )}

      {/* Goal Info + Key Stats (compact) */}
      <Card className="shadow-sm" bodyStyle={{ padding: '14px 20px' }}>
        <div className="flex flex-wrap items-center justify-between gap-x-8 gap-y-3">
          <div className="flex items-center gap-4 min-w-0">
            <div className="flex-shrink-0 flex items-center">
              <Progress
                type="circle"
                percent={userProgramPortfolio.progress}
                format={percent => `${formatNumber(percent)}%`}
                size={64}
              />
            </div>
            <div className="min-w-0 flex flex-col justify-center">
              <Title level={5} className="my-0 text-gray-800 truncate">
                {userGoal.name}
              </Title>
              <Text type="secondary" className="text-xs">
                {formatDateSafe(userGoal.start_date)} →{' '}
                {formatDateSafe(userGoal.end_date)}
              </Text>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-2 items-center self-center">
            <CompactStat
              label="Target Amount"
              value={formatNumber(userGoal.target_amount)}
            />
            <CompactStat
              label="Subscription Amount"
              value={formatNumber(totalSubscriptionAmount)}
            />
            <CompactStat
              label="Market Value"
              value={formatNumber(totalMarketValue)}
            />
            <CompactStat
              label="Total P/L"
              value={
                <>
                  {formatNumber(totalGainLossAmount)}{' '}
                  <span className="text-xs">
                    ({formatNumber(totalGainLossPercentage)}%)
                  </span>
                </>
              }
              color={gainLossColor(totalGainLossAmount)}
            />
          </div>
        </div>
      </Card>

      {/* Performance vs VNINDEX */}
      <TwrVsMoneyWeightedNote
        twrReturnPct={twrReturnPct}
        moneyWeightedReturnPct={totalGainLossPercentage}
      />
      <PriceDataStatusAlert items={priceDataStatus} />
      <GoalPerformanceChart
        title="Hiệu suất so với VNINDEX (chuẩn hóa = 100)"
        points={performancePoints}
        isLoading={isLoadingPerformanceChart}
        showExplanation
      />
      <GoalPerformanceChart
        title="Hiệu suất theo tuần so với VNINDEX (chuẩn hóa = 100)"
        points={performancePoints}
        isLoading={isLoadingPerformanceChart}
        weekly
      />

      {/* Portfolio Holdings Tables */}
      {portfolioFundItems.length > 0 && (
        <Card
          className="shadow-sm"
          title={
            <Title level={5} className="mb-0 text-gray-800">
              Fund Holdings
            </Title>
          }
        >
          <Table
            rowKey="id"
            columns={[
              {
                title: 'Code',
                dataIndex: 'product_code',
                className: 'font-medium',
              },
            ].concat(sharedColumns)}
            dataSource={portfolioFundItems}
            pagination={false}
            className="mt-4"
            size="middle"
          />
        </Card>
      )}

      {portfolioStockItems.length > 0 && (
        <Card
          className="shadow-sm"
          title={
            <Title level={5} className="mb-0 text-gray-800">
              Stock Holdings
            </Title>
          }
        >
          <Table
            rowKey="id"
            columns={[
              {
                title: 'Symbol',
                dataIndex: 'symbol',
                className: 'font-medium',
              },
            ].concat(sharedColumns)}
            dataSource={portfolioStockItems}
            pagination={false}
            className="mt-4"
            size="middle"
          />
        </Card>
      )}

      {/* Allocation breakdowns, scoped to this goal's stock holdings only */}
      <Card
        className="shadow-sm"
        title={
          <Title level={5} className="mb-0 text-gray-800">
            Phân bổ theo ngành
          </Title>
        }
      >
        <IndustryAllocationChart byIndustry={byIndustry} />
        <Table
          rowKey="industry_code"
          columns={industryColumns}
          dataSource={byIndustry}
          pagination={false}
          className="mt-4"
          size="middle"
        />
      </Card>

      <Card
        className="shadow-sm"
        title={
          <Title level={5} className="mb-0 text-gray-800">
            Phân bổ theo mã cổ phiếu/trái phiếu
          </Title>
        }
      >
        <SymbolAllocationChart bySymbol={bySymbol} />
        <Table
          rowKey="symbol"
          columns={symbolAllocationColumns}
          dataSource={bySymbol}
          pagination={false}
          className="mt-4"
          size="middle"
        />
      </Card>

      {/* Transactions */}
      <Card
        className="shadow-sm"
        title={
          <Title level={5} className="mb-0 text-gray-800">
            Transactions
          </Title>
        }
      >
        <Table
          rowKey="id"
          loading={isLoadingTransactions}
          columns={transactionColumns}
          dataSource={transactions}
          className="mt-4"
          size="middle"
          pagination={{ pageSize: 24, showSizeChanger: false }}
        />
      </Card>
    </div>
  );
};

export default GoalPortfolio;
